import { Component, OnInit, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { ReportService } from '../services/report.service';
import { TopCancelledProduct } from '../services/models/top-cancelled-product-output';
import { ThemeService } from '../services/theme.service';

type ChartKind = 'bar' | 'treemap' | 'donut';

@Component({
  selector: 'app-top-cancelled-products-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, NgApexchartsModule, TranslocoModule],
  templateUrl: './top-cancelled-products-chart.component.html',
  styleUrl: './top-cancelled-products-chart.component.css'
})
export class TopCancelledProductsChartComponent implements OnInit, OnDestroy {

  loading: boolean = false;
  period: number = 2; // 1=7 dias, 2=15 dias (default), 3=30 dias, 4=vida
  top: number = 20;   // default
  chartType: ChartKind = 'donut';
  chartOptions: any;
  lastUpdated?: string;
  private cachedData: TopCancelledProduct[] = [];
  private langSub?: Subscription;

  constructor(
    private reportService: ReportService,
    private transloco: TranslocoService,
    private themeService: ThemeService
  ) {
    // Recompõe o gráfico ao alternar o tema (legibilidade no claro/escuro)
    effect(() => {
      this.themeService.resolvedTheme();
      if (this.cachedData.length) this.buildChart(this.cachedData);
    });
  }

  /** Tema do ApexCharts derivado do tema resolvido (claro/escuro). */
  private chartTheme(): { mode: 'light' | 'dark' } {
    return { mode: this.themeService.resolvedTheme() };
  }

  ngOnInit(): void {
    this.restorePreferences();
    this.fetchAndBuild();
    this.langSub = this.transloco.langChanges$.subscribe(() => {
      if (this.cachedData.length) this.buildChart(this.cachedData);
    });
  }

  onPeriodChange(): void {
    this.fetchAndBuild();
  }

  onTopChange(): void {
    // Sanitiza limites razoáveis
    if (!this.top || this.top < 1) this.top = 1;
    const maxTop = this.chartType === 'donut' ? 20 : 200;
    if (this.top > maxTop) this.top = maxTop;
    this.fetchAndBuild();
  }

  onChartTypeChange(): void {
    this.persistPreferences();
    this.buildChart(this.cachedData);
  }

  setChartType(kind: ChartKind): void {
    if (this.chartType === kind) return;
    this.chartType = kind;
    // Ajusta automaticamente o top se necessário para Donut
    if (this.chartType === 'donut' && this.top > 20) {
      this.top = 20;
    }
    this.onChartTypeChange();
  }

  private fetchAndBuild(): void {
    this.loading = true;
    this.persistPreferences();
    this.reportService.getTopCancelledProducts(this.period, this.top)
      .subscribe({
        next: (resp) => {
          const list = (resp.body || []).slice().sort((a, b) => b.totalQuantity - a.totalQuantity);
          this.cachedData = list;
          this.lastUpdated = this.extractLastUpdated(list);
          this.buildChart(list);
          this.loading = false;
        },
        error: () => {
          this.cachedData = [];
          this.chartOptions = this.buildEmptyChart();
          this.loading = false;
        }
      });
  }

  private buildChart(data: TopCancelledProduct[]): void {
    const names = data.map(x => x.productTitle);
    const values = data.map(x => x.totalQuantity);

    if (this.chartType === 'bar') {
      const dynamicHeight = Math.max(300, values.length * 20);
      const mobileHeight = Math.max(250, values.length * 18);
      this.chartOptions = {
        series: [{ name: this.transloco.translate('charts.quantityCancelled'), data: values }],
        theme: this.chartTheme(),
        chart: {
          type: 'bar',
          height: dynamicHeight,
          animations: { enabled: true },
          toolbar: { show: false }
        },
        plotOptions: { bar: { horizontal: true } },
        xaxis: { 
          categories: names.map(n => this.truncate(n, 28)),
          labels: { style: { fontSize: '12px' } }
        },
        dataLabels: { enabled: false },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: false,
          followCursor: false,
          hideDelay: 100,
          fixed: {
            enabled: false
          },
          y: { formatter: (val: number) => `${val}` },
          custom: ({ seriesIndex, dataPointIndex, w }: any) => {
            const full = names[dataPointIndex];
            const value = values[dataPointIndex];
            return `<div style="padding:6px; pointer-events: none;">${full}<br/><strong>${value}</strong></div>`;
          }
        },
        title: { text: this.transloco.translate('charts.topCancelled'), align: 'center', style: { fontSize: '14px' } },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: { height: mobileHeight },
            xaxis: { 
              categories: names.map(n => this.truncate(n, 20)),
              labels: { style: { fontSize: '10px' } }
            },
            title: { style: { fontSize: '12px' } }
          }
        }]
      };
    }

    if (this.chartType === 'treemap') {
      this.chartOptions = {
        series: [{ data: names.map((n, i) => ({ x: n, y: values[i] })) }],
        theme: this.chartTheme(),
        chart: {
          type: 'treemap',
          height: 400,
          toolbar: { show: false }
        },
        legend: { show: false },
        title: { text: this.transloco.translate('charts.topCancelledTreemap'), align: 'center', style: { fontSize: '14px' } },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: false,
          followCursor: false,
          hideDelay: 100,
          fixed: {
            enabled: false
          },
          y: { formatter: (val: number) => `${val}` }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: { height: 300 },
            title: { style: { fontSize: '12px' } }
          }
        }]
      };
    }

    if (this.chartType === 'donut') {
      // Calcula altura baseada no número de itens para acomodar a legenda
      const baseHeight = 380;
      const legendRows = Math.ceil(names.length / 3); // Aproximadamente 3 itens por linha
      const legendHeight = legendRows * 25; // ~25px por linha
      const calculatedHeight = baseHeight + legendHeight;
      
      this.chartOptions = {
        series: values,
        theme: this.chartTheme(),
        labels: names.map(n => this.truncate(n, 16)),
        chart: {
          type: 'donut',
          height: calculatedHeight,
          toolbar: { show: false }
        },
        legend: { 
          show: true, 
          position: 'bottom',
          fontSize: '12px',
          itemMargin: { horizontal: 5, vertical: 5 }
        },
        dataLabels: { enabled: false },
        title: { text: this.transloco.translate('charts.topCancelledDonut'), align: 'center', style: { fontSize: '14px' } },
        tooltip: {
          enabled: true,
          shared: false,
          intersect: false,
          followCursor: false,
          hideDelay: 100,
          fixed: {
            enabled: false
          },
          custom: ({ seriesIndex }: any) => {
            const full = names[seriesIndex];
            const value = values[seriesIndex];
            return `<div style="padding:6px; pointer-events: none;">${full}<br/><strong>${value}</strong></div>`;
          }
        },
        responsive: [{
          breakpoint: 768,
          options: {
            chart: { 
              height: Math.max(300, 250 + Math.ceil(names.length / 2) * 20) // 2 itens por linha no mobile
            },
            legend: { 
              position: 'bottom',
              fontSize: '10px',
              itemMargin: { horizontal: 3, vertical: 3 }
            },
            title: { style: { fontSize: '12px' } },
            labels: names.map(n => this.truncate(n, 12))
          }
        }]
      };
    }
  }

  private buildEmptyChart(): any {
    return {
      series: [{ name: this.transloco.translate('charts.quantityCancelled'), data: [] }],
      theme: this.chartTheme(),
      chart: { type: 'bar', height: 350 },
      plotOptions: { bar: { horizontal: true } },
      xaxis: { categories: [] },
      dataLabels: { enabled: false },
      title: { text: this.transloco.translate('charts.topCancelled'), align: 'center' }
    };
  }

  private truncate(text: string, max: number): string {
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
  }

  private extractLastUpdated(data: TopCancelledProduct[]): string | undefined {
    if (!data || data.length === 0) return new Date().toISOString();
    const newest = data.reduce((acc, cur) => {
      return new Date(cur.lastUpdated) > new Date(acc.lastUpdated) ? cur : acc;
    });
    return newest.lastUpdated;
  }

  private persistPreferences(): void {
    try {
      localStorage.setItem('tcp.period', String(this.period));
      localStorage.setItem('tcp.top', String(this.top));
      localStorage.setItem('tcp.chartType', this.chartType);
    } catch { /* ignore storage errors */ }
  }

  private restorePreferences(): void {
    try {
      const p = Number(localStorage.getItem('tcp.period'));
      const t = Number(localStorage.getItem('tcp.top'));
      const ct = (localStorage.getItem('tcp.chartType') as ChartKind) || 'donut';
      if ([1,2,3,4].includes(p)) this.period = p;
      if (t && t >= 1 && t <= 200) this.top = t;
      this.chartType = ct;
    } catch { /* ignore */ }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }
}



